import { ModalContent, ModalDrawer, ModalHeader } from '@core/components/Modal';
import { css } from '@emotion/react';
import React, { Fragment, memo, useMemo, useCallback } from 'react';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';

import { ProposalForm } from '../../ProposalForm';
import { FormInput, FormColorPicker, FormSelect, FormAjaxSelect } from '@core/components/form';
import { useBloc } from '@core/utils/bloc';
import { CommonService, ProposalService } from '@business/services';
import { GoogleAPI } from '@business/api/google_api';
import { FontResponse } from '@business/entities/google';
import { map } from 'rxjs/operators';
import { Col, Row } from 'reactstrap';
import { BlockTitleFormatSettings } from './BlockTitleFormatSettings';
import { Tabs, TabsMapping } from '@modules/common/Tabs';

const styles = {
  content: css`
    padding: 25px !important;
    background-color: #f8f8fb;
  `,
};

export interface ProposalSettingsOverlayProps {
  form: ProposalForm;
}

// General Settings tab — the proposal-wide options (unchanged, previously the whole drawer)
const GeneralSettings = memo((props: ProposalSettingsOverlayProps) => {
  const { form } = props;
  const formValue = useNonNilObservable(form.value$);

  const onSearch = useCallback((search: string) => {
    return GoogleAPI.fonts(search).pipe(map(fonts => CommonService.selectItems(fonts, 'family')));
  }, []);

  return (
    <Fragment>
      <FormInput control={form.controls.title} />
      <Row>
        <Col>
          <FormAjaxSelect
            control={form.controls.font}
            callback={search => onSearch(search)}
            initalValue={formValue.font ? { label: formValue.font.family, value: formValue.font } : undefined}
          />
        </Col>
        <Col>
          <FormSelect control={form.controls.fontWeight} items={ProposalService.getAllFontWeight()} isClearable />
        </Col>
      </Row>
      <FormInput control={form.controls.fontSize} />
      <FormInput control={form.controls.headingFontSize} />
      <FormColorPicker control={form.controls.primaryColor} />
      <FormInput control={form.controls.carouselSectionDropDownSize} />
      <FormColorPicker control={form.controls.carouselSectionColor} />
    </Fragment>
  );
});

export const ProposalSettingsOverlay = memo((props: ProposalSettingsOverlayProps) => {
  const { form } = props;

  const tabs: TabsMapping[] = useMemo(
    () => [
      { title: 'General Settings', component: GeneralSettings, props: { form } },
      // Block-level styling shared across sections — currently the Block Title Formats
      { title: 'Block Settings', component: BlockTitleFormatSettings, props: { form } },
    ],
    [form],
  );

  return (
    <ModalDrawer maxWidth="lg">
      <ModalHeader title="Settings" />
      <ModalContent customCss={styles.content}>
        <Tabs tabs={tabs} />
      </ModalContent>
    </ModalDrawer>
  );
});
